import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import { formatEnumLabel, getAiVerdictBadgeVariant, getAiVerdictLabel } from './helpers';
import MetricPill from './MetricPill';
import type { ObvAiAuditResult } from './types';

interface AiAuditCardProps {
    isRunDisabled: boolean;
    isRunPending: boolean;
    latestAiAuditResult: ObvAiAuditResult | null;
    onRunAiAudit: () => Promise<void>;
    renderUrls: { focused: string; wide: string } | null;
}

const AiAuditCard = ({ isRunDisabled, isRunPending, latestAiAuditResult, onRunAiAudit, renderUrls }: AiAuditCardProps) => (
    <Card responsivePadding bodyClassName="space-y-4" title="AI Audit">
        <div className="text-sm text-zinc-300">
            This runs a structured vision review against the current OBV audit view using a focused render and a wide render. It is a
            secondary reviewer, not the source of truth, but it is useful for catching residual UI ambiguity before final signoff.
        </div>

        <div className="flex flex-wrap gap-2">
            {renderUrls ? (
                <>
                    <Button size="sm" to={renderUrls.focused} variant="secondary">
                        Open Focused Render
                    </Button>
                    <Button size="sm" to={renderUrls.wide} variant="secondary">
                        Open Wide Render
                    </Button>
                </>
            ) : null}
            <Button disabled={isRunDisabled} isLoading={isRunPending} size="sm" type="button" variant="primary" onClick={onRunAiAudit}>
                Run AI Audit For Current View
            </Button>
        </div>

        {latestAiAuditResult ? (
            <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/70 p-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={getAiVerdictBadgeVariant(latestAiAuditResult.review.overallVerdict)}>
                        {getAiVerdictLabel(latestAiAuditResult.review.overallVerdict)}
                    </Badge>
                    <Badge variant="dark">confidence {latestAiAuditResult.review.confidence.toFixed(2)}</Badge>
                    <Badge variant="secondary">semantics {formatEnumLabel(latestAiAuditResult.review.semanticAssessment)}</Badge>
                    <Badge variant="secondary">UI {formatEnumLabel(latestAiAuditResult.review.uiReadability)}</Badge>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricPill label="Reviewed Focus DateTime" value={latestAiAuditResult.resolvedFocusDateTime ?? 'n/a'} />
                    <MetricPill label="Reviewer Model" value={latestAiAuditResult.reviewerModelKey} />
                    <MetricPill label="Focused Events" value={String(latestAiAuditResult.selectedEvents.length)} />
                    <MetricPill label="Overall Verdict" value={getAiVerdictLabel(latestAiAuditResult.review.overallVerdict)} />
                </div>

                <div className="rounded-lg border border-zinc-800 bg-black/20 p-4 text-sm text-zinc-300">
                    {latestAiAuditResult.review.notes}
                </div>

                {latestAiAuditResult.selectedEvents.length > 0 ? (
                    <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Reviewed Focus Markers</div>
                        <div className="flex flex-wrap gap-2">
                            {latestAiAuditResult.selectedEvents.map((selectedEvent) => (
                                <Badge
                                    key={`${selectedEvent.markerId}:${selectedEvent.eventType}`}
                                    variant={
                                        selectedEvent.tone === 'bullish'
                                            ? 'success'
                                            : selectedEvent.tone === 'bearish'
                                              ? 'danger'
                                              : 'secondary'
                                    }
                                >
                                    {selectedEvent.markerId} = {formatEnumLabel(selectedEvent.eventType)}
                                </Badge>
                            ))}
                        </div>
                    </div>
                ) : null}

                {latestAiAuditResult.review.focusedEventAssessments.length > 0 ? (
                    <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Focused Event Assessments</div>
                        <div className="grid gap-3 md:grid-cols-2">
                            {latestAiAuditResult.review.focusedEventAssessments.map((assessment) => (
                                <div
                                    className="rounded-lg border border-zinc-800 bg-black/20 p-4"
                                    key={`${assessment.markerId}:${assessment.eventType}`}
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge variant="dark">{assessment.markerId}</Badge>
                                        <Badge
                                            variant={
                                                assessment.plausibility === 'plausible'
                                                    ? 'success'
                                                    : assessment.plausibility === 'inconsistent'
                                                      ? 'danger'
                                                      : 'secondary'
                                            }
                                        >
                                            {formatEnumLabel(assessment.plausibility)}
                                        </Badge>
                                        <Badge variant="secondary">{formatEnumLabel(assessment.eventType)}</Badge>
                                    </div>
                                    <div className="mt-3 text-sm text-zinc-300">{assessment.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {latestAiAuditResult.review.issueCodes.length > 0 ? (
                    <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Issue Codes</div>
                        <div className="flex flex-wrap gap-2">
                            {latestAiAuditResult.review.issueCodes.map((issueCode) => (
                                <Badge key={issueCode} variant="dark">
                                    {formatEnumLabel(issueCode)}
                                </Badge>
                            ))}
                        </div>
                    </div>
                ) : null}

                {latestAiAuditResult.review.recommendedEdits.length > 0 ? (
                    <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Recommended Edits</div>
                        <div className="space-y-2 text-sm text-zinc-300">
                            {latestAiAuditResult.review.recommendedEdits.map((recommendation) => (
                                <div key={recommendation}>• {recommendation}</div>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                    <a
                        className="text-sm text-blue-400 hover:text-blue-300 underline"
                        href={latestAiAuditResult.renderUrls.focused}
                        rel="noreferrer"
                        target="_blank"
                    >
                        Exact Focused Render Reviewed
                    </a>
                    <a
                        className="text-sm text-blue-400 hover:text-blue-300 underline"
                        href={latestAiAuditResult.renderUrls.wide}
                        rel="noreferrer"
                        target="_blank"
                    >
                        Exact Wide Render Reviewed
                    </a>
                </div>
            </div>
        ) : (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-4 text-sm text-zinc-400">
                No OBV AI audit has been run for the current page state yet.
            </div>
        )}
    </Card>
);

export default AiAuditCard;
